import { MolsonBox, MolsonTypography } from '@components/reusable'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { molsonStyled, molsonUseTheme } from '@theme'

const StyledToggleButtonGroup = molsonStyled(ToggleButtonGroup)(
  ({ theme }) => ({
    position: 'relative',
    border: `1px solid ${theme.palette.secondary[400]}`,
    borderRadius: '100px !important',
    backgroundColor: theme.palette.background.paper,
    '& .sliding-selection': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      transition: 'transform 0.3s ease',
      zIndex: 0
    }
  })
)

const StyledToggleButton = molsonStyled(ToggleButton)({
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent !important'
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  '& > *': {
    zIndex: 1,
    position: 'relative'
  }
})

export type CustomTogglerOption = {
  value: number
  label: string
  ariaLabel: string
  disabled?: boolean
}

export type CustomTogglerProps = {
  options: [CustomTogglerOption, CustomTogglerOption]
  value: number
  onChange: (event: React.MouseEvent<HTMLElement>, value: number | null) => void
  buttonWidth?: number
  exclusive?: boolean
}

export const CustomToggler = ({
  options,
  value,
  onChange,
  buttonWidth = 120,
  exclusive = true
}: CustomTogglerProps) => {
  const theme = molsonUseTheme()

  return (
    <MolsonBox alignSelf="flex-end">
      <StyledToggleButtonGroup
        value={value}
        exclusive={exclusive}
        onChange={onChange}
      >
        <div
          className="sliding-selection"
          style={{
            transform: `translateX(${value}px)`,
            background: theme.palette.primary.lightest,
            outline: `1px solid ${theme.palette.primary.light}`,
            border: '1px solid transparent',
            borderRadius: '100px',
            width: `${buttonWidth}px`,
            height: '100%'
          }}
        />
        {options.map((option, index) => (
          <StyledToggleButton
            key={option.value}
            disableRipple={true}
            disableFocusRipple={true}
            disableTouchRipple={true}
            value={option.value}
            aria-label={option.ariaLabel}
            sx={{
              width: `${buttonWidth}px`,
              color: option.disabled
                ? `${theme.palette.secondary[400]} !important`
                : 'black !important',
              pointerEvents: option.disabled ? 'none' : 'auto',
              borderTopLeftRadius: index === 0 ? '100px !important' : 'none',
              borderBottomLeftRadius: index === 0 ? '100px !important' : 'none',
              borderTopRightRadius:
                index === options.length - 1 ? '100px !important' : 'none',
              borderBottomRightRadius:
                index === options.length - 1 ? '100px !important' : 'none',
              border: 'none !important',
              textTransform: 'capitalize !important',
              paddingLeft: '16px !important',
              paddingRight: '16px !important'
            }}
          >
            <MolsonTypography fontWeight="bold" variant="body2">
              {option.label}
            </MolsonTypography>
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </MolsonBox>
  )
}
